import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { PageLoading } from 'ui-toolkit';
import { PolicyTypeEnum } from '../classes/PolicyTypeEnum';
import IRouterParams from '../IRouterParams';
import { RootStore } from '../services/RootStore';
import getConfig from '../utils/getConfig';
import getText from '../utils/getText';
import PolicyReview from './PolicyReview';
import { clientUrls } from '../../ui-toolkit/rest/ClientUrlFactory';
import { ModalHandler } from 'policyUI/context/components/ModalHandler';

interface IContainerPolicyReview extends RouteComponentProps<IRouterParams> {
  rootStore: RootStore;
}

class ContainerPolicyReview extends React.Component<IContainerPolicyReview, {}> {
  private policyType = this.props.match!.params.policyType;
  private orderId = this.props.match!.params.orderId;
  private action = this.props.match!.params.action;

  constructor(props: IContainerPolicyReview) {
    super(props);
    this.initPolicyData();
  }

  private initPolicyData = async () => {
    await this.props.rootStore.policyPreviewService.initPolicyPreview(this.policyType, this.orderId);
  };

  public render() {
    return (
      <>
        {this.props.rootStore.policyPreviewService.getPageLoading ? null : (
          <>
            <ModalHandler savePage={this.savePolicy} loadPage={this.initPolicyData} />
            <PolicyReview
              history={this.props.history}
              location={this.props.location}
              match={this.props.match}
              title={getText(`policy.${this.action}.${this.policyType}`)}
              showEndorsements={getConfig(`config.policy.showEndorsements.${this.policyType}`)}
              showMortgageeClause={getConfig(`config.policy.mortgageeClause.${this.policyType}`)}
              getPolicyJacket={this.props.rootStore.policyScheduleAService.getPolicyJacket}
              onBackClick={this.goBack}
              onFinalizeClick={this.goFinalizeClick}
              onJacketUpload={this.onJacketUpload}
              onDeletePolicyJacket={this.deletePolicyJacket}
              onUpdatePageClick={this.onUpdatePage}
              orderDetails={this.props.rootStore.policyScheduleAService.getOrderDetails}
              recordedDeed={this.props.rootStore.policyScheduleAService.getRecordedDeeds}
              getEndorsements={this.props.rootStore.policyScheduleAService.getEndorsements}
              pageLoading={this.props.rootStore.policyScheduleAService.getloadPage.fetching!}
              pageErrorMessage={
                this.props.rootStore.policyPreviewService.getPageErrorMessage ||
                this.props.rootStore.policyScheduleAService.getPageError
              }
              editAddressUrl={clientUrls.urlFor.orderEditAddress(this.orderId)}
              editAmtOfInsuredURL={clientUrls.urlFor.orderEditTransaction(
                this.orderId,
                this.policyType === PolicyTypeEnum.owner ? 'purchase_price' : 'loan_amount'
              )}
              editBorrowerPage={clientUrls.urlFor.orderStatus(this.orderId)}
              cdBuilderPage={clientUrls.urlFor.feesTab(this.orderId)}
              policyUpdateSuccess={this.props.rootStore.policyScheduleAService.getPolicyNumberFetch.success!}
              policyUpdateFetching={this.props.rootStore.policyScheduleAService.getPolicyNumberFetch.fetching!}
              policyJacketFetching={this.props.rootStore.policyScheduleAService.getPolicyJacketFetch.fetching!}
              policyJacketSuccess={this.props.rootStore.policyScheduleAService.getPolicyJacketFetch.success!}
              showUpdateSpinner={this.props.rootStore.policyScheduleAService.getUpdatePageFetch.fetching!}
              showUpdateSuccess={this.props.rootStore.policyScheduleAService.getUpdatePageFetch.success!}
              disableFinalizeButton={!this.props.rootStore.policyScheduleAService.scheduleAIsValid}
              finalizeInProcess={this.props.rootStore.policyPreviewService.getFinalizeInProcess}
              finalizedSuccess={this.props.rootStore.policyPreviewService.getFinalizedSuccess}
              policyJacketError={this.props.rootStore.policyScheduleAService.getPolicyJacketError}
              onPolicyNumberBlur={this.savePolicy}
              onChangePolicyNumber={this.onChangePolicyNumber}
              isAmericanDigital={this.props.rootStore.policyScheduleAService.isAmericanDigital}
              recordedMortgage={this.props.rootStore.policyScheduleAService.getUserRecordedMortgage}
            />
          </>
        )}
        <PageLoading
          isLoading={
            this.props.rootStore.policyPreviewService.getPageLoading ||
            this.props.rootStore.policyScheduleAService.getloadPage.fetching! ||
            this.props.rootStore.policyScheduleAService.getPolicyNumberFetch.fetching! ||
            this.props.rootStore.policyScheduleAService.getPolicyJacketFetch.fetching! ||
            this.props.rootStore.policyPreviewService.getFinalizeInProcess
          }
        />
      </>
    );
  }

  private onJacketUpload = async (files: FileList | null) => {
    await this.props.rootStore.policyScheduleAService.onPolicyJacketUpload(files, this.orderId);
  };

  private goBack = () => {
    this.props.rootStore.policyScheduleAService.policyJacketFetchSetDefault();
    this.props.history.push({
      pathname: clientUrls.urlFor.policyGenerationB(this.orderId, this.policyType, this.action),
    });
  };

  private goFinalizeClick = async () => {
    await this.props.rootStore.policyPreviewService.finalizeOrder(this.policyType, this.orderId);
  };

  private deletePolicyJacket = () => {
    this.props.rootStore.policyScheduleAService.onDeletePolicyJacket(this.policyType, this.orderId);
  };

  private savePolicy = async () => {
    await this.props.rootStore.policyScheduleAService.savePolicy(this.orderId);
  };

  private onChangePolicyNumber = async (newValue: string | null, error?: string | null) => {
    await this.props.rootStore.policyScheduleAService.setPolicyNumber(newValue, this.policyType, error);
  };

  private onUpdatePage = async () => {
    await this.props.rootStore.policyScheduleAService.updatePage(this.policyType, this.orderId);
  };
}

export default inject('rootStore')(observer(ContainerPolicyReview));
